import _ from 'lodash';
import { useReducer } from 'react';
import { SfdrCategoryId, UnitCategoryCode, PurchasableFund } from 'funds-model';
import selectorsOptions from './fundListOptions';

// Helper functions moved from utils/funds - passed as props
const getTargetAndEsgMarket = (mifidTargetMarket: string) => {
  const targetMarketCode = mifidTargetMarket.charAt(0) || '';
  const esgMarketCode = mifidTargetMarket.charAt(1) || '';
  return { targetMarketCode, esgMarketCode };
};

const isFundInTargetMarket = (fund: PurchasableFund, targetMarketCode: string) =>
    fund.targetMarkets ? fund.targetMarkets.map((tm: any) => tm.code).includes(targetMarketCode) : false;

const isFundInEsgMarket = (fund: PurchasableFund, esgMarketCode: string) =>
    esgMarketCode === '' || (fund.esgMarket !== null && fund.esgMarket !== undefined && fund.esgMarket.id <= Number(esgMarketCode));

const isObjectEmpty = (obj: Object) => {
  return Object.entries(obj).length === 0 && obj.constructor === Object;
};

type Payload = {
  value?: string[],
  filterType?: string
};

interface State {
  [index: string]: string[] | Object | undefined;
}

export interface FilterState extends State {
  fundType: string[];
  risk: string[];
  umbrella: string[];
  horizon: Object;
  search: string;
  unitCategoryCode: UnitCategoryCode;
  adequacyWithTargetMarket?: boolean;
  showOnlyInWallet?: boolean;
  showOnlyEsg?: boolean;
}

const isFundInTargetAndEsgMarket = (fund: PurchasableFund, customerTargetMarketCode: string): boolean => {
  const { targetMarketCode, esgMarketCode } = getTargetAndEsgMarket(customerTargetMarketCode);
  return isFundInTargetMarket(fund, targetMarketCode) && isFundInEsgMarket(fund, esgMarketCode)
}

const getFilteredFunds = (funds: PurchasableFund[] | undefined, filters: FilterState, hasSearchFilter = true, customerTargetMarketCode?: string) => {
  if (!funds || funds?.length === 0) {
    return undefined;
  }

  let filteredFunds = [...funds];

  Object.keys(filters).forEach(filterName => {
    const currentFilter = filters[filterName];

    if (filterName === 'unitCategoryCode') {
      return;
    }

    if (currentFilter === undefined || (currentFilter as string[]).length === 0 || isObjectEmpty(currentFilter)) {
      return;
    }

    if (filterName === 'horizon') {
      filteredFunds = filteredFunds.filter((fund: PurchasableFund) => (currentFilter as number[]).includes(fund.horizonValue))
      return filteredFunds;
    }

    if (filterName === 'search' && hasSearchFilter) {
      filteredFunds = filteredFunds.filter((fund: PurchasableFund) => fund.name.toLowerCase().includes((currentFilter as string).toLowerCase()));
      return filteredFunds;
    }

    if (filterName === 'search' && !hasSearchFilter) {
      return filteredFunds;
    }

    if (filterName === 'adequacyWithTargetMarket' && customerTargetMarketCode) {
      const adequacyWithTargetMarketFilter = currentFilter;

      filteredFunds = filteredFunds.filter((fund: PurchasableFund) => adequacyWithTargetMarketFilter
        ? isFundInTargetAndEsgMarket(fund, customerTargetMarketCode)
        : filteredFunds);
      return filteredFunds;
    }

    if (filterName === 'showOnlyInWallet') {
      filteredFunds = currentFilter ? filteredFunds.filter((fund: PurchasableFund) => fund.amountOwned) : filteredFunds;
      return filteredFunds;
    }

    if (filterName === 'showOnlyEsg') {
      filteredFunds = currentFilter
        ? filteredFunds.filter(fund => fund.sfdrCategoryId === SfdrCategoryId.ART_8 || fund.sfdrCategoryId === SfdrCategoryId.ART_9)
        : filteredFunds;
      return filteredFunds;
    }

    // filter for array type: risk, umbrella and fundType
    filteredFunds = filteredFunds.filter((fund: any) => {
      return fund[filterName] ? (currentFilter as string[]).includes(fund[filterName].name.toLowerCase()) : false;
    });
  });
  return filteredFunds;
};

export const filterInitialState = {
  fundType: [],
  risk: [],
  umbrella: [],
  horizon: [],
  search: '',
  unitCategoryCode: UnitCategoryCode.A,
  adequacyWithTargetMarket: undefined,
  showOnlyInWallet: undefined,
  showOnlyEsg: undefined,
};

const filterReducer = (state: FilterState, action: { type: string, payload?: string[] | Object }) => {
  switch (action.type) {
    case 'ADD_FUNDTYPE_FILTER':
      return {...state, fundType: [...action.payload as string[]]};
    case 'ADD_RISK_FILTER':
      return {...state, risk: [...action.payload as string[]]};
    case 'ADD_UMBRELLA_FILTER':
      return {...state, umbrella: [...action.payload as string[]]};
    case 'ADD_HORIZON_FILTER':
      const horizonNumbersArray = [...action.payload as string[]].map(payloadItem => {
        return selectorsOptions.horizon.find(opt => opt.label === payloadItem)?.filter.number;
      })
      return {...state, horizon: horizonNumbersArray};
    case 'ADD_SEARCH_FILTER':
      return {...state, search: action.payload as string};
    case 'ADD_UNIT_CATEGORY_FILTER':
      return {...state, unitCategoryCode: action.payload as UnitCategoryCode};
    case 'ADD_ADEQUACY_WITH_TARGET_MARKET_FILTER':
      return {...state, adequacyWithTargetMarket: action.payload as boolean};
    case 'ADD_SHOW_ONLY_IN_WALLET_FILTER':
      return {...state, showOnlyInWallet: action.payload as boolean};
    case 'ADD_SHOW_ONLY_ESG_FILTER':
      return {...state, showOnlyEsg: action.payload as boolean};
    case 'REMOVE_FILTER':
      const { filterType, value } = action.payload as { filterType: string, value: string};
      if (filterType === 'horizon') {
        const horizonNumberValue = selectorsOptions.horizon.find(obj => obj.label === value)?.filter.number
        return {...state, horizon: (state.horizon as string[]).filter((v: string) => Number(v) !== horizonNumberValue)}
      };
      if (filterType === 'search') { return {...state, search: ''} };
      if (filterType === 'autocomplete') { return {...state, search: ''} };
      if (filterType === 'adequacyWithTargetMarket') { return {...state, adequacyWithTargetMarket: undefined} };
      if (filterType === 'showOnlyInWallet') { return {...state, showOnlyInWallet: undefined} };
      if (filterType === 'showOnlyEsg') { return {...state, showOnlyEsg: undefined} };
      return {...state, [filterType]: (state[filterType] as string[]).filter((v: string) => v.toLowerCase() !== value.toLowerCase()) };
    case 'CLEAR_ALL_FILTERS':
      return filterInitialState;
    default:
      return state;
  }
};

const useFilterState = (customInitialState?: FilterState) => {
  const [filters, dispatch] = useReducer(filterReducer, customInitialState ?? filterInitialState);
  const isInitialFilter = _.isEqual(filters, filterInitialState);
  const makeAddFilterAction = (name: string) => (payload: Payload) => {
    dispatch({ type: `ADD_${name}_FILTER`, payload });
  };

  const addFundTypeFilter = makeAddFilterAction('FUNDTYPE');
  const addRiskFilter = makeAddFilterAction('RISK');
  const addUmbrellaFilter = makeAddFilterAction('UMBRELLA');
  const addHorizonFilter = makeAddFilterAction('HORIZON');
  const addSearchFilter = makeAddFilterAction('SEARCH');
  const addUnitCategoryFilter = makeAddFilterAction('UNIT_CATEGORY');
  const addAdequacyWithTargetMarketFilter = makeAddFilterAction('ADEQUACY_WITH_TARGET_MARKET');
  const addWalletFilter = makeAddFilterAction('SHOW_ONLY_IN_WALLET');
  const addEsgFilter = makeAddFilterAction('SHOW_ONLY_ESG');

  const removeFilter = (payload: Payload) => {
    dispatch({ type: 'REMOVE_FILTER', payload });
  };

  const clearFilters = () => {
    dispatch({ type: 'CLEAR_ALL_FILTERS' });
  };

  const actions = {
    addFundTypeFilter,
    addRiskFilter,
    addUmbrellaFilter,
    addHorizonFilter,
    addSearchFilter,
    addUnitCategoryFilter,
    addAdequacyWithTargetMarketFilter,
    addWalletFilter,
    addEsgFilter,
    removeFilter,
    clearFilters
  };
  return { filters, actions, getFilteredFunds, isInitialFilter };
};

export { useFilterState };
